import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ColegioService from '../services/ColegioService';
import ParticularService from '../services/ParticularService';
import UsuarioService from '../services/UsuarioService';
import { useAuth } from '../contexts/AuthContext';
import colegioApiService from '../services/ColegioApiService';
import profesorParticularApiService from '../services/ProfesorParticularApiService';
import pwaAppsService, { PWA_APP_OPTIONS, getPwaAppOption } from '../services/PwaAppsService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import AdminComunicacionesModal from './common/AdminComunicacionesModal';
import LoadingSpinner from './common/LoadingSpinner';
import ProcesosAsincronicosModal from './common/ProcesosAsincronicosModal';
import TableExportActions from './common/TableExportActions';
import { isAdminColegio } from '../utils/adminMenu';
import { toBooleanPermission } from '../utils/particularChatGPTAccess';
import './AdminSelectColegio.css';
import './AdminProTheme.css';

const readFirstValue = (source, keys) => {
  if (!source) return undefined;
  return keys.map((key) => source[key]).find((value) => value !== undefined && value !== null);
};

const toDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultConsumosRange = () => {
  const now = new Date();
  return {
    from: toDateInput(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: toDateInput(now)
  };
};

const formatDateInputLabel = (value) => {
  if (!value) return '—';
  const [year, month, day] = String(value).split('-');
  return year && month && day ? `${day}/${month}/${year}` : value;
};

const PARTICULAR_LIMIT_FIELDS = [
  { key: 'limiteAlumnos', apiKey: 'LimiteAlumnos', label: 'Alumnos permitidos', icon: 'fas fa-user-graduate', step: '1' },
  { key: 'limiteProfesores', apiKey: 'LimiteProfesores', label: 'Profesores asociados permitidos', icon: 'fas fa-user-tie', step: '1' },
  { key: 'limiteCursos', apiKey: 'LimiteCursos', label: 'Cursos permitidos', icon: 'fas fa-layer-group', step: '1' },
  { key: 'limiteAulas', apiKey: 'LimiteAulas', label: 'Aulas permitidas', icon: 'fas fa-door-open', step: '1' },
  { key: 'limiteAlmacenamientoGb', apiKey: 'LimiteAlmacenamientoGb', label: 'GB de almacenamiento', icon: 'fas fa-database', step: '0.01' }
];

const emptyParticularConfigForm = PARTICULAR_LIMIT_FIELDS.reduce((state, field) => ({
  ...state,
  [field.key]: ''
}), {
  permiteChatGPT: false,
  cobrosMercadoPago: false,
  calendario: false,
  modoColegio: false
});

// Una cuenta de particular se muestra con el nombre de su institución; si no tiene, con el del administrador (dueño).
const getParticularNombreVisible = (particular) => (
  particular?.nombreVisible
  || particular?.NombreVisible
  || particular?.institucionNombre
  || particular?.InstitucionNombre
  || particular?.nombre
  || particular?.Nombre
  || 'Sin nombre'
);

const getParticularAdministrador = (particular) => {
  const nombre = `${particular?.nombreUsuario || particular?.NombreUsuario || ''} ${particular?.apellidoUsuario || particular?.ApellidoUsuario || ''}`.trim();
  return nombre || particular?.nombre || particular?.Nombre || '';
};

const readParticularLimit = (particular, field) => (
  readFirstValue(particular, [field.key, field.apiKey])
);

const normalizeParticularLimitInput = (value, allowDecimal = false) => {
  if (value === '' || value === null || value === undefined) return null;
  const normalized = String(value).replace(',', '.');
  const number = allowDecimal ? Number(normalized) : parseInt(normalized, 10);
  return Number.isFinite(number) && number >= 0 ? number : null;
};

const buildPwaUpdateResult = (response, appOption) => {
  const requiresUpdate = Boolean(readFirstValue(response, ['requiresUpdate', 'RequiresUpdate']));
  const notificationSent = readFirstValue(response, ['notificationSent', 'NotificationSent']);
  const message = readFirstValue(response, ['message', 'Message']);
  const appLabel = appOption?.title || 'PWA';

  const lines = [
    requiresUpdate
      ? `La actualización de ${appLabel} fue solicitada correctamente.`
      : 'La API respondió correctamente, pero no dejó la app lista para actualizar.'
  ];

  if (notificationSent === true) {
    lines.push('Se envió la notificación push a los dispositivos suscriptos.');
  } else if (notificationSent === false) {
    lines.push('No se solicitó el envío de notificación push.');
  }

  if (message) {
    lines.push(`Mensaje que verán los usuarios: ${message}`);
  }

  return {
    type: requiresUpdate ? 'success' : 'warning',
    title: requiresUpdate ? 'Actualización solicitada' : 'Respuesta recibida',
    lines
  };
};

const AdminSelectColegio = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateToken, user, logout } = useAuth();
  const [colegios, setColegios] = useState([]);
  const [particulares, setParticulares] = useState([]);
  const [particularesLoading, setParticularesLoading] = useState(false);
  const [particularesError, setParticularesError] = useState('');
  const [entityScope, setEntityScope] = useState(() => (
    location.state?.scope === 'particulares' ? 'particulares' : 'colegios'
  ));
  const [viewMode, setViewMode] = useState('cards');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionMessage, setActionMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [ffModal, setFfModal] = useState({ open: false, colegio: null, items: [], loading: false, error: '' });
  const [consumosModal, setConsumosModal] = useState({
    open: false,
    colegio: null,
    data: null,
    loading: false,
    error: '',
    filters: getDefaultConsumosRange()
  });
  const [confirmModal, setConfirmModal] = useState({ open: false, colegio: null, nextActivo: true, loading: false });
  const [particularConfigModal, setParticularConfigModal] = useState({
    open: false,
    particular: null,
    form: emptyParticularConfigForm,
    saving: false,
    error: ''
  });
  const [resetSchemaModal, setResetSchemaModal] = useState({
    open: false,
    colegio: null,
    confirmText: '',
    loading: false,
    understood: false
  });
  const [accessingColegio, setAccessingColegio] = useState({ loading: false, nombre: '' });
  const [showAccessInbox, setShowAccessInbox] = useState(false);
  const [accessRequests, setAccessRequests] = useState([]);
  const [accessPage, setAccessPage] = useState(1);
  const [accessTotal, setAccessTotal] = useState(0);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessLoadingMore, setAccessLoadingMore] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [accessSelected, setAccessSelected] = useState(null);
  const [accessDetailLoading, setAccessDetailLoading] = useState(false);
  const [accessUnreadCount, setAccessUnreadCount] = useState(0);
  const [accessSearch, setAccessSearch] = useState('');
  const [showComunicacionesModal, setShowComunicacionesModal] = useState(false);
  const [showProcesosModal, setShowProcesosModal] = useState(false);
  const [focusProcesoId, setFocusProcesoId] = useState(null);
  const [pwaUpdateModal, setPwaUpdateModal] = useState({
    open: false,
    loading: false,
    saving: false,
    error: '',
    appKey: PWA_APP_OPTIONS[0].key,
    mensaje: PWA_APP_OPTIONS[0].defaultMessage
  });
  const [pwaUpdateResultModal, setPwaUpdateResultModal] = useState({
    open: false,
    type: 'success',
    title: '',
    lines: []
  });
  const isGeneralAdmin = !isAdminColegio(user);
  const isParticularesScope = isGeneralAdmin && entityScope === 'particulares';

  const filteredAccessRequests = useMemo(() => {
    if (!accessSearch.trim()) return accessRequests;
    const term = accessSearch.toLowerCase();
    return accessRequests.filter((item) => {
      const nombre = (item.nombre || '').toLowerCase();
      const apellido = (item.apellido || '').toLowerCase();
      const email = (item.email || '').toLowerCase();
      const consulta = (item.consulta || '').toLowerCase();
      return (
        nombre.includes(term) ||
        apellido.includes(term) ||
        email.includes(term) ||
        consulta.includes(term)
      );
    });
  }, [accessRequests, accessSearch]);

  const formatDateTime = (value) => {
    if (!value) return '—';
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredColegios = useMemo(() => {
    if (!searchTerm.trim()) return colegios;
    const term = searchTerm.toLowerCase();
    return colegios.filter((reg) => {
      const name = (reg.nombre || reg.name || '').toLowerCase();
      const desc = (reg.descripcion || reg.detalle || '').toLowerCase();
      return name.includes(term) || desc.includes(term);
    });
  }, [colegios, searchTerm]);

  const filteredParticulares = useMemo(() => {
    if (!searchTerm.trim()) return particulares;
    const term = searchTerm.toLowerCase();
    return particulares.filter((reg) => {
      const name = getParticularNombreVisible(reg).toLowerCase();
      const admin = getParticularAdministrador(reg).toLowerCase();
      const desc = (reg.descripcion || reg.Descripcion || '').toLowerCase();
      const email = (reg.email || reg.Email || '').toLowerCase();
      const emailAdmin = (reg.emailUsuario || reg.EmailUsuario || '').toLowerCase();
      const cuit = (reg.cuit || reg.Cuit || '').toLowerCase();
      return [name, admin, desc, email, emailAdmin, cuit].some((value) => value.includes(term));
    });
  }, [particulares, searchTerm]);

  useEffect(() => {
    if (location.state?.scope === 'particulares' || location.state?.scope === 'colegios') {
      setEntityScope(location.state.scope);
    }
  }, [location.state?.scope]);

  useEffect(() => {
    const fetchColegios = async () => {
      setIsLoading(true);
      setError('');
      setActionMessage({ type: '', text: '' });
      try {
      const response = isAdminColegio(user)
        ? await colegioApiService.getMisColegios()
        : await colegioApiService.getColegios();
      const list = response.colegios || response.Colegios || response.data || response || [];
      const normalized = Array.isArray(list) ? list : [];
      setColegios(normalized);
      ColegioService.setColegioData({ colegios: normalized, areas: [], materias: [] });
      } catch (err) {
        console.error('❌ Error cargando colegios:', err);
        setError(err.message || 'No pudimos obtener los colegios.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchColegios();
  }, []);

  const loadParticulares = useCallback(async () => {
    if (!isGeneralAdmin) return;
    setParticularesLoading(true);
    setParticularesError('');
    setActionMessage({ type: '', text: '' });
    try {
      const response = await profesorParticularApiService.getProfesoresParticulares();
      const list = response?.profesoresParticulares || response?.ProfesoresParticulares || response?.data || response || [];
      const normalized = Array.isArray(list) ? list : [];
      setParticulares(normalized);
      ParticularService.setParticulares(normalized);
    } catch (err) {
      setParticularesError(err.message || 'No pudimos obtener los particulares.');
    } finally {
      setParticularesLoading(false);
    }
  }, [isGeneralAdmin]);

  useEffect(() => {
    if (isParticularesScope) {
      loadParticulares();
    }
  }, [isParticularesScope, loadParticulares]);

  const fetchAccessUnreadCount = async () => {
    if (!isGeneralAdmin) return;
    try {
      const response = await UsuarioService.getAccessRequestUnreadCount();
      const total = response?.total ?? response?.Total ?? 0;
      setAccessUnreadCount(total);
    } catch (err) {
      // silencioso
    }
  };

  const loadAccessRequests = async (page = 1, append = false) => {
    if (!isGeneralAdmin) return;
    try {
      if (append) {
        setAccessLoadingMore(true);
      } else {
        setAccessLoading(true);
        setAccessError('');
      }
      const response = await UsuarioService.getAccessRequests(page, 50);
      const rawItems = response?.items || response?.Items || [];
      const items = Array.isArray(rawItems)
        ? rawItems.map((item) => ({
            id: item.id || item.Id,
            nombre: item.nombre || item.Nombre,
            apellido: item.apellido || item.Apellido,
            email: item.email || item.Email,
            telefono: item.telefono || item.Telefono,
            consulta: item.consulta || item.Consulta,
            fechaRecibido: item.fechaRecibido || item.FechaRecibido || item.fechaAlta || item.FechaAlta,
            leido: item.leido ?? item.Leido ?? false
          }))
        : [];
      const total = response?.total ?? response?.Total ?? items.length;
      const unread = response?.unreadCount ?? response?.UnreadCount ?? 0;
      setAccessRequests(prev => (append ? [...prev, ...items] : items));
      setAccessPage(page);
      setAccessTotal(total);
      setAccessUnreadCount(unread);
    } catch (err) {
      setAccessError(err.message || 'No pudimos cargar las solicitudes.');
    } finally {
      setAccessLoading(false);
      setAccessLoadingMore(false);
    }
  };

  const handleOpenAccessInbox = () => {
    setShowAccessInbox(true);
    setAccessSelected(null);
    loadAccessRequests(1, false);
  };

  const handleCloseAccessInbox = () => {
    setShowAccessInbox(false);
  };

  const handleSelectAccessRequest = async (item) => {
    if (!item?.id) return;
    setAccessDetailLoading(true);
    try {
      const detail = await UsuarioService.getAccessRequestById(item.id);
      const normalized = detail
        ? {
            id: detail.id || detail.Id,
            nombre: detail.nombre || detail.Nombre,
            apellido: detail.apellido || detail.Apellido,
            email: detail.email || detail.Email,
            telefono: detail.telefono || detail.Telefono,
            consulta: detail.consulta || detail.Consulta,
            fechaRecibido: detail.fechaRecibido || detail.FechaRecibido || detail.fechaAlta || detail.FechaAlta,
            leido: detail.leido ?? detail.Leido ?? true,
            fechaLeido: detail.fechaLeido || detail.FechaLeido,
            idUsuarioLeido: detail.idUsuarioLeido || detail.IdUsuarioLeido,
            nombreUsuarioLeido: detail.nombreUsuarioLeido || detail.NombreUsuarioLeido,
            apellidoUsuarioLeido: detail.apellidoUsuarioLeido || detail.ApellidoUsuarioLeido
          }
        : null;
      setAccessSelected(normalized);
      if (!item.leido) {
        setAccessRequests(prev =>
          prev.map(r => (r.id === item.id ? { ...r, leido: true } : r))
        );
        setAccessUnreadCount(prev => (prev > 0 ? prev - 1 : 0));
      }
    } catch (err) {
      setAccessError(err.message || 'No pudimos obtener el mensaje.');
    } finally {
      setAccessDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchAccessUnreadCount();
  }, [isGeneralAdmin]);

  useEffect(() => {
    if (!isGeneralAdmin) return;
    const intervalId = setInterval(() => {
      fetchAccessUnreadCount();
    }, 60000);
    return () => clearInterval(intervalId);
  }, [isGeneralAdmin]);

  const handleToggleColegioActivo = async (colegio) => {
    const colegioId = colegio?.id || colegio?.Id;
    if (!colegioId) return;
    const actualActivo = Boolean(colegio?.activo ?? colegio?.Activo ?? true);
    const nextActivo = !actualActivo;
    setConfirmModal({ open: true, colegio, nextActivo, loading: false });
  };

  const handleEntityScopeChange = (scope) => {
    setEntityScope(scope);
    setSearchTerm('');
    setActionMessage({ type: '', text: '' });
  };

  const loadPwaUpdateConfig = async (appKey) => {
    const appOption = getPwaAppOption(appKey);

    setPwaUpdateModal((prev) => ({
      ...prev,
      appKey: appOption.key,
      open: true,
      loading: true,
      saving: false,
      error: '',
      mensaje: appOption.defaultMessage
    }));

    try {
      const response = await pwaAppsService.getVersion(appOption.key);
      const message = response?.message || response?.Message || '';

      setPwaUpdateModal((prev) => ({
        ...prev,
        loading: false,
        appKey: appOption.key,
        mensaje: message || appOption.defaultMessage
      }));
    } catch (err) {
      setPwaUpdateModal((prev) => ({
        ...prev,
        loading: false,
        error: err?.message || 'No pudimos leer la configuración actual de la PWA.'
      }));
    }
  };

  const openPwaUpdateModal = async () => {
    await loadPwaUpdateConfig(pwaUpdateModal.appKey || PWA_APP_OPTIONS[0].key);
  };

  const handlePwaUpdateAppChange = (appKey) => {
    if (pwaUpdateModal.saving || pwaUpdateModal.loading) return;
    loadPwaUpdateConfig(appKey);
  };

  const closePwaUpdateModal = () => {
    if (pwaUpdateModal.saving) return;
    setPwaUpdateModal((prev) => ({ ...prev, open: false, error: '' }));
  };

  const closePwaUpdateResultModal = () => {
    setPwaUpdateResultModal({ open: false, type: 'success', title: '', lines: [] });
  };

  const savePwaUpdateConfig = async () => {
    setPwaUpdateModal((prev) => ({ ...prev, saving: true, error: '' }));
    setActionMessage({ type: '', text: '' });

    try {
      const appOption = getPwaAppOption(pwaUpdateModal.appKey);
      const response = await pwaAppsService.updateVersion(appOption.key, {
        requiereActualizacion: true,
        mensaje: pwaUpdateModal.mensaje.trim() || appOption.defaultMessage
      });
      setActionMessage({
        type: 'success',
        text: `La actualización de ${appOption.title} fue solicitada correctamente.`
      });
      setPwaUpdateModal((prev) => ({ ...prev, open: false, saving: false }));
      setPwaUpdateResultModal({
        open: true,
        ...buildPwaUpdateResult(response, appOption)
      });
    } catch (err) {
      const errorMessage = err?.message || 'No pudimos guardar la configuración de actualización.';
      setPwaUpdateModal((prev) => ({
        ...prev,
        saving: false,
        error: errorMessage
      }));
      setPwaUpdateResultModal({
        open: true,
        type: 'error',
        title: 'No se pudo solicitar la actualización',
        lines: [errorMessage]
      });
    }
  };

  const closeConfirmModal = () => {
    if (confirmModal.loading) return;
    setConfirmModal({ open: false, colegio: null, nextActivo: true, loading: false });
  };

  const openResetSchemaModal = (colegio) => {
    setResetSchemaModal({
      open: true,
      colegio,
      confirmText: '',
      loading: false,
      understood: false
    });
  };

  const closeResetSchemaModal = () => {
    if (resetSchemaModal.loading) return;
    setResetSchemaModal({
      open: false,
      colegio: null,
      confirmText: '',
      loading: false,
      understood: false
    });
  };

  const getResetSchemaColegioNombre = (colegio = resetSchemaModal.colegio) => (
    colegio?.nombre
    || colegio?.Nombre
    || colegio?.name
    || ''
  ).trim();

  const isResetSchemaNameMatch = (() => {
    const expected = getResetSchemaColegioNombre();
    if (!expected) return false;
    return resetSchemaModal.confirmText.trim().toLowerCase() === expected.toLowerCase();
  })();

  const canConfirmResetSchema = resetSchemaModal.understood && isResetSchemaNameMatch;

  const confirmToggleColegio = async () => {
    const colegioId = confirmModal.colegio?.id || confirmModal.colegio?.Id;
    if (!colegioId) return;
    const nextActivo = confirmModal.nextActivo;
    setConfirmModal((prev) => ({ ...prev, loading: true }));
    setActionMessage({ type: '', text: '' });

    try {
      await colegioApiService.updateColegioActivo(colegioId, nextActivo);
      setColegios((prev) =>
        prev.map((item) => {
          const itemId = item?.id || item?.Id;
          if (itemId === colegioId) {
            return { ...item, Activo: nextActivo, activo: nextActivo };
          }
          return item;
        })
      );
      setActionMessage({
        type: 'success',
        text: nextActivo
          ? 'El colegio fue activado correctamente.'
          : 'El colegio fue desactivado correctamente.'
      });
      setConfirmModal({ open: false, colegio: null, nextActivo: true, loading: false });
    } catch (err) {
      setConfirmModal((prev) => ({ ...prev, loading: false }));
      setActionMessage({
        type: 'error',
        text: err?.message || 'No pudimos actualizar el estado del colegio.'
      });
    }
  };

  const handleToggleParticularActivo = async (particular) => {
    const particularId = particular?.id || particular?.Id;
    if (!particularId) return;

    const nextActivo = !Boolean(particular?.activo ?? particular?.Activo ?? true);
    setActionMessage({ type: '', text: '' });

    try {
      await profesorParticularApiService.updateProfesorParticularActivo(particularId, nextActivo);
      setParticulares((prev) =>
        prev.map((item) => {
          const itemId = item?.id || item?.Id;
          if (itemId === particularId) {
            return { ...item, Activo: nextActivo, activo: nextActivo };
          }
          return item;
        })
      );
      setActionMessage({
        type: 'success',
        text: nextActivo
          ? 'El particular fue activado correctamente.'
          : 'El particular fue desactivado correctamente.'
      });
    } catch (err) {
      setActionMessage({
        type: 'error',
        text: err?.message || 'No pudimos actualizar el estado del particular.'
      });
    }
  };

  const openParticularConfig = (particular) => {
    const form = PARTICULAR_LIMIT_FIELDS.reduce((state, field) => {
      const value = readParticularLimit(particular, field);
      return {
        ...state,
        [field.key]: value === undefined || value === null ? '' : String(value)
      };
    }, {
      permiteChatGPT: toBooleanPermission(readFirstValue(particular, [
        'permiteChatGPT',
        'PermiteChatGPT',
        'permiteChatGpt',
        'PermiteChatGpt'
      ])),
      cobrosMercadoPago: toBooleanPermission(readFirstValue(particular, [
        'cobrosMercadoPago',
        'CobrosMercadoPago'
      ])),
      calendario: toBooleanPermission(readFirstValue(particular, [
        'calendario',
        'Calendario'
      ])),
      modoColegio: toBooleanPermission(readFirstValue(particular, [
        'modoColegio',
        'ModoColegio'
      ]))
    });

    setParticularConfigModal({
      open: true,
      particular,
      form,
      saving: false,
      error: ''
    });
  };

  const closeParticularConfig = () => {
    if (particularConfigModal.saving) return;
    setParticularConfigModal({
      open: false,
      particular: null,
      form: emptyParticularConfigForm,
      saving: false,
      error: ''
    });
  };

  const handleParticularConfigChange = (event) => {
    const { checked, name, type, value } = event.target;
    setParticularConfigModal((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: type === 'checkbox' ? checked : value
      },
      error: ''
    }));
  };

  const saveParticularConfig = async (event) => {
    event.preventDefault();
    const particular = particularConfigModal.particular;
    const particularId = particular?.id || particular?.Id;
    if (!particularId) return;

    const payload = PARTICULAR_LIMIT_FIELDS.reduce((state, field) => {
      const allowDecimal = field.key === 'limiteAlmacenamientoGb';
      return {
        ...state,
        [field.key]: normalizeParticularLimitInput(particularConfigModal.form[field.key], allowDecimal)
      };
    }, {
      permiteChatGPT: Boolean(particularConfigModal.form.permiteChatGPT),
      cobrosMercadoPago: Boolean(particularConfigModal.form.cobrosMercadoPago),
      calendario: Boolean(particularConfigModal.form.calendario),
      modoColegio: Boolean(particularConfigModal.form.modoColegio)
    });

    setParticularConfigModal((prev) => ({ ...prev, saving: true, error: '' }));
    setActionMessage({ type: '', text: '' });

    try {
      const response = await profesorParticularApiService.updateProfesorParticularConfiguracion(particularId, payload);
      const updated = response?.profesorParticular || response?.ProfesorParticular || response?.data || null;
      setParticulares((prev) => prev.map((item) => {
        const itemId = item?.id || item?.Id;
        return itemId === particularId
          ? {
              ...item,
              ...(updated || {}),
              ...payload,
              PermiteChatGPT: payload.permiteChatGPT,
              CobrosMercadoPago: payload.cobrosMercadoPago,
              Calendario: payload.calendario,
              ModoColegio: payload.modoColegio
            }
          : item;
      }));
      setActionMessage({
        type: 'success',
        text: response?.mensaje || response?.Mensaje || 'Configuración del particular actualizada correctamente.'
      });
      setParticularConfigModal({
        open: false,
        particular: null,
        form: emptyParticularConfigForm,
        saving: false,
        error: ''
      });
    } catch (err) {
      setParticularConfigModal((prev) => ({
        ...prev,
        saving: false,
        error: err?.message || 'No pudimos guardar la configuración del particular.'
      }));
    }
  };

  const handleAdministrarParticular = async (particular) => {
    const particularId = particular?.id || particular?.Id;
    const nombre = getParticularNombreVisible(particular);

    if (!particularId) {
      setActionMessage({ type: 'error', text: 'No pudimos identificar el particular seleccionado.' });
      return;
    }

    setAccessingColegio({ loading: true, nombre });
    setActionMessage({ type: '', text: '' });

    try {
      ColegioService.setActiveColegio(null);
      ParticularService.setParticulares(particulares);
      ParticularService.setActiveParticular({ ...particular, nombre });

      const res = await UsuarioService.selectParticular(particularId);
      const token = res?.token || res?.Token;
      const featureFlags = res?.ffColegio || res?.FFColegio || [];

      if (token) {
        updateToken(token);
      }
      ColegioService.setFeatureFlags(Array.isArray(featureFlags) ? featureFlags : []);

      navigate('/particular', { replace: true });
    } catch (err) {
      ParticularService.setActiveParticular(null);
      setActionMessage({
        type: 'error',
        text: err?.message || `No pudimos administrar ${nombre}.`
      });
    } finally {
      setAccessingColegio({ loading: false, nombre: '' });
    }
  };

  const confirmResetSchema = async () => {
    const colegioId = resetSchemaModal.colegio?.id || resetSchemaModal.colegio?.Id;
    const colegioNombre = getResetSchemaColegioNombre();
    if (!colegioId || !colegioNombre || !canConfirmResetSchema) return;

    setResetSchemaModal((prev) => ({ ...prev, loading: true }));
    setActionMessage({ type: '', text: '' });

    try {
      const response = await colegioApiService.resetColegioSchema(
        colegioId,
        resetSchemaModal.confirmText.trim()
      );
      setActionMessage({
        type: 'success',
        text: response?.mensaje || `El esquema del colegio "${colegioNombre}" se reseteó correctamente.`
      });
      setResetSchemaModal({
        open: false,
        colegio: null,
        confirmText: '',
        loading: false,
        understood: false
      });
    } catch (err) {
      setResetSchemaModal((prev) => ({ ...prev, loading: false }));
      setActionMessage({
        type: 'error',
        text: err?.message || `No pudimos resetear el esquema del colegio "${colegioNombre}".`
      });
    }
  };

  const handleSelect = (colegio) => {
    const colegioId = colegio?.id || colegio?.Id;
    const colegioNombre = colegio?.nombre || colegio?.name || 'colegio';

    setAccessingColegio({ loading: true, nombre: colegioNombre });
    ParticularService.setActiveParticular(null);
    ColegioService.setActiveColegio(colegio);

    if (!colegioId) {
      navigate('/admin-dashboard', { replace: true });
      return;
    }

    const fetchFeatureFlags = async () => {
      try {
        const featureResponse = await colegioApiService.getFeatureFlagsByColegio(colegioId);
        const flags = featureResponse.featureFlags || featureResponse.FeatureFlags || [];
        ColegioService.setFeatureFlags(flags);
      } catch (flagError) {
        console.warn('No se pudieron cargar las feature flags del colegio:', flagError);
        ColegioService.setFeatureFlags([]);
      }
    };

    fetchFeatureFlags()
      .finally(() => {
        UsuarioService.selectColegio(colegioId)
          .then((res) => {
            const token = res.token || res.Token;
            if (token) {
              updateToken(token);
            }
            navigate('/admin-dashboard', { replace: true });
          })
          .catch(() => {
            navigate('/admin-dashboard', { replace: true });
          });
      });
  };

  const openFeatureFlags = async (colegio) => {
    const colegioId = colegio?.id || colegio?.Id;
    if (!colegioId) return;
    setFfModal({ open: true, colegio, items: [], loading: true, error: '' });
    try {
      const featureResponse = await colegioApiService.getFeatureFlagsByColegio(colegioId);
      const items = featureResponse.featureFlags || featureResponse.FeatureFlags || [];
      setFfModal({ open: true, colegio, items: Array.isArray(items) ? items : [], loading: false, error: '' });
    } catch (err) {
      setFfModal({
        open: true,
        colegio,
        items: [],
        loading: false,
        error: err?.message || 'No se pudieron cargar las feature flags.'
      });
    }
  };

  const closeFeatureFlags = () => {
    setFfModal({ open: false, colegio: null, items: [], loading: false, error: '' });
  };

  const loadConsumos = async (colegio, filters) => {
    const colegioId = colegio?.id || colegio?.Id;
    if (!colegioId) return;

    setConsumosModal((prev) => ({
      ...prev,
      open: true,
      colegio,
      filters,
      loading: true,
      error: ''
    }));

    try {
      const response = await colegioApiService.getConsumosTokensByColegio(colegioId, filters);
      setConsumosModal((prev) => ({
        ...prev,
        open: true,
        colegio,
        filters,
        data: response,
        loading: false,
        error: ''
      }));
    } catch (err) {
      setConsumosModal((prev) => ({
        ...prev,
        open: true,
        colegio,
        filters,
        data: null,
        loading: false,
        error: err?.message || 'No se pudieron cargar los consumos.'
      }));
    }
  };

  const openConsumos = async (colegio) => {
    if (!isGeneralAdmin) return;
    const colegioId = colegio?.id || colegio?.Id;
    if (!colegioId) return;
    await loadConsumos(colegio, getDefaultConsumosRange());
  };

  const closeConsumos = () => {
    setConsumosModal({
      open: false,
      colegio: null,
      data: null,
      loading: false,
      error: '',
      filters: getDefaultConsumosRange()
    });
  };

  const handleConsumosFilterChange = (event) => {
    const { name, value } = event.target;
    setConsumosModal((prev) => ({
      ...prev,
      filters: {
        ...(prev.filters || getDefaultConsumosRange()),
        [name]: value
      }
    }));
  };

  const handleConsumosFilterSubmit = (event) => {
    event.preventDefault();
    const filters = consumosModal.filters || getDefaultConsumosRange();
    if (filters.from && filters.to && filters.to < filters.from) {
      setConsumosModal((prev) => ({
        ...prev,
        error: 'La fecha hasta no puede ser menor a la fecha desde.'
      }));
      return;
    }

    loadConsumos(consumosModal.colegio, filters);
  };

  const monthLabels = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
  const formatNumber = (value) => {
    const numeric = Number(value || 0);
    return Number.isFinite(numeric) ? numeric.toLocaleString('es-AR') : '0';
  };
  const formatUsd = (value) => {
    const numeric = Number(value || 0);
    return Number.isFinite(numeric)
      ? `USD ${numeric.toLocaleString('es-AR', { minimumFractionDigits: 4, maximumFractionDigits: 6 })}`
      : 'USD 0,0000';
  };

  const handleToggleFeatureFlag = async (flag) => {
    const colegioId = ffModal.colegio?.id || ffModal.colegio?.Id;
    if (!colegioId) return;
    const nombre = flag?.nombre || flag?.Nombre;
    if (!nombre) return;

    const nextActivo = !(flag?.activo ?? flag?.Activo);
    setFfModal((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        const itemNombre = item?.nombre || item?.Nombre;
        if (itemNombre === nombre) {
          return { ...item, Activo: nextActivo, activo: nextActivo };
        }
        return item;
      }),
      error: ''
    }));

    try {
      await colegioApiService.updateFeatureFlagByColegio(colegioId, nombre, nextActivo);
    } catch (err) {
      setFfModal((prev) => ({
        ...prev,
        items: prev.items.map((item) => {
          const itemNombre = item?.nombre || item?.Nombre;
          if (itemNombre === nombre) {
            return { ...item, Activo: !nextActivo, activo: !nextActivo };
          }
          return item;
        }),
        error: err?.message || 'No se pudo actualizar el feature flag.'
      }));
    }
  };

  const renderCards = () => (
    <div className="admin-colegio-grid">
      {filteredColegios.map((reg) => {
        const colegioId = reg.id || reg.Id;
        const activo = Boolean(reg?.activo ?? reg?.Activo ?? true);
        return (
          <div
            key={colegioId || reg.nombre}
            className={`colegio-card${activo ? '' : ' colegio-card-inactive'}`}
          >
            <div className="card-head">
              <div className="badge">Colegio</div>
              <i className="fas fa-map-marker-alt" />
            </div>
            <h3>{reg.nombre || reg.name || 'Sin nombre'}</h3>
            {reg.descripcion && <p className="desc">{reg.descripcion}</p>}
            {reg.detalle && <p className="desc">{reg.detalle}</p>}
            <div className="card-footer">
              <button
                type="button"
                className="card-action primary"
                onClick={() => handleSelect(reg)}
                title="Administrar"
              >
                Administrar
                <i className="fas fa-arrow-right" />
              </button>
              <button
                type="button"
                className="card-action"
                onClick={() => colegioId && navigate(`/admin/colegios/${colegioId}/admins/nuevo`)}
                disabled={!colegioId}
                title="Administradores"
              >
                <i className="fas fa-user-plus" />
              </button>
              {isGeneralAdmin && (
                <button
                  type="button"
                  className="card-action"
                  onClick={() => openFeatureFlags(reg)}
                  disabled={!colegioId}
                  title="Feature flags"
                >
                  <i className="fas fa-toggle-on" />
                </button>
              )}
              {isGeneralAdmin && (
                <button
                  type="button"
                  className="card-action"
                  onClick={() => colegioId && navigate(`/admin/colegios/${colegioId}/estadisticas`)}
                  disabled={!colegioId}
                  title="Estadísticas"
                >
                  <i className="fas fa-chart-line" />
                </button>
              )}
              {isGeneralAdmin && (
                <button
                  type="button"
                  className="card-action"
                  onClick={() => openConsumos(reg)}
                  disabled={!colegioId}
                  title="Consumos"
                >
                  <i className="fas fa-chart-bar" />
                </button>
              )}
              {isGeneralAdmin && (
                <button
                  type="button"
                  className="card-action"
                  onClick={() => colegioId && navigate(`/admin/colegios/${colegioId}/openai-assistants-config`)}
                  disabled={!colegioId}
                  title="Asistentes OpenAI fijos"
                >
                  <i className="fas fa-robot" />
                </button>
              )}
              {isGeneralAdmin && (
                <button
                  type="button"
                  className={`card-action ${activo ? 'danger' : 'success'}`}
                  onClick={() => handleToggleColegioActivo(reg)}
                  disabled={!colegioId}
                  title={activo ? 'Desactivar colegio' : 'Activar colegio'}
                >
                  <i className={`fas ${activo ? 'fa-ban' : 'fa-check'}`} />
                </button>
              )}
              {isGeneralAdmin && (
                <button
                  type="button"
                  className="card-action danger reset-schema-trigger"
                  onClick={() => openResetSchemaModal(reg)}
                  disabled={!colegioId}
                  title="Resetear esquema"
                >
                  <i className="fas fa-rotate-left" />
                  <span>Resetear esquema</span>
                </button>
              )}
              {isGeneralAdmin && (
                <button
                  type="button"
                  className="card-action"
                  onClick={() => colegioId && navigate(`/admin/colegios/${colegioId}/editar`)}
                  disabled={!colegioId}
                  title="Editar"
                >
                  <i className="fas fa-pen" />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderParticularesCards = () => (
    <div className="admin-colegio-grid">
      {filteredParticulares.map((reg) => {
        const particularId = reg.id || reg.Id;
        const activo = Boolean(reg?.activo ?? reg?.Activo ?? true);
        const nombre = getParticularNombreVisible(reg);
        const tieneInstitucion = Boolean(reg.institucionNombre || reg.InstitucionNombre);
        const administrador = getParticularAdministrador(reg);
        const cantidadAdministradores = Number(reg.cantidadAdministradores ?? reg.CantidadAdministradores ?? 1);
        const limitesConfigurados = PARTICULAR_LIMIT_FIELDS
          .filter((field) => readParticularLimit(reg, field) !== undefined && readParticularLimit(reg, field) !== null)
          .length;
        return (
          <div
            key={particularId || reg.email || reg.Email || nombre}
            className={`colegio-card${activo ? '' : ' colegio-card-inactive'}`}
          >
            <div className="card-head">
              <div className="badge">{tieneInstitucion ? 'Institución' : 'Particular'}</div>
              <i className={`fas ${tieneInstitucion ? 'fa-building' : 'fa-user-tie'}`} />
            </div>
            <h3>{nombre}</h3>
            <div className="school-meta">
              <span>
                <i className="fas fa-user-shield" />
                {administrador ? `Administrador: ${administrador}` : 'Sin administrador'}
                {cantidadAdministradores > 1 ? ` (+${cantidadAdministradores - 1})` : ''}
              </span>
              <span><i className="fas fa-envelope" /> {reg.email || reg.Email || 'Sin email'}</span>
              <span>
                <i className="fas fa-sliders-h" />
                {limitesConfigurados > 0 ? `${limitesConfigurados}/5 límites configurados` : 'Sin límites configurados'}
              </span>
            </div>
            <div className="card-footer">
              <button
                type="button"
                className="card-action primary"
                onClick={() => handleAdministrarParticular(reg)}
                disabled={!particularId}
                title="Administrar"
              >
                Administrar
                <i className="fas fa-arrow-right" />
              </button>
              <button
                type="button"
                className="card-action"
                onClick={() => particularId && navigate(`/admin/particulares/${particularId}/administradores`)}
                disabled={!particularId}
                title="Administradores"
              >
                <i className="fas fa-user-shield" />
              </button>
              <button
                type="button"
                className="card-action"
                onClick={() => particularId && navigate(`/admin/particulares/${particularId}/profesores`)}
                disabled={!particularId}
                title="Profesores"
              >
                <i className="fas fa-user-plus" />
              </button>
              <button
                type="button"
                className="card-action config"
                onClick={() => openParticularConfig(reg)}
                disabled={!particularId}
                title="Configuración"
              >
                <i className="fas fa-cog" />
              </button>
              <button
                type="button"
                className="card-action"
                onClick={() => particularId && navigate(`/admin/particulares/${particularId}/editar`)}
                disabled={!particularId}
                title="Editar"
              >
                <i className="fas fa-pen" />
              </button>
              <button
                type="button"
                className={`card-action ${activo ? 'danger' : 'success'}`}
                onClick={() => handleToggleParticularActivo(reg)}
                disabled={!particularId}
                title={activo ? 'Desactivar particular' : 'Activar particular'}
              >
                <i className={`fas ${activo ? 'fa-ban' : 'fa-check'}`} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderTable = () => (
    <div className="admin-table-wrapper">
      <table id="admin-select-colegio-table" className="admin-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Descripción</th>
            <th>Administradores</th>
            {isGeneralAdmin && <th>Editar</th>}
            {isGeneralAdmin && <th>Feature flags</th>}
            {isGeneralAdmin && <th>Estadísticas</th>}
            {isGeneralAdmin && <th>Consumos</th>}
            {isGeneralAdmin && <th>Asistentes fijos</th>}
            {isGeneralAdmin && <th>Reset esquema</th>}
            {isGeneralAdmin && <th>Estado</th>}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filteredColegios.map((reg) => {
            const activo = Boolean(reg?.activo ?? reg?.Activo ?? true);
            return (
            <tr key={reg.id || reg.Id || reg.nombre} className={activo ? '' : 'row-inactive'}>
              <td>{reg.nombre || reg.name || 'Sin nombre'}</td>
              <td>{reg.descripcion || reg.detalle || '-'}</td>
              <td className="actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => (reg.id || reg.Id) && navigate(`/admin/colegios/${reg.id || reg.Id}/admins/nuevo`)}
                  type="button"
                  disabled={!reg.id && !reg.Id}
                  title="Administradores"
                >
                  <i className="fas fa-user-plus" />
                </button>
              </td>
              {isGeneralAdmin && (
                <td className="actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => (reg.id || reg.Id) && navigate(`/admin/colegios/${reg.id || reg.Id}/editar`)}
                    type="button"
                    disabled={!reg.id && !reg.Id}
                    title="Editar"
                  >
                    <i className="fas fa-pen" />
                  </button>
                </td>
              )}
              {isGeneralAdmin && (
                <td className="actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => openFeatureFlags(reg)}
                    type="button"
                    disabled={!reg.id && !reg.Id}
                    title="Feature flags"
                  >
                    <i className="fas fa-toggle-on" />
                  </button>
                </td>
              )}
              {isGeneralAdmin && (
                <td className="actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => (reg.id || reg.Id) && navigate(`/admin/colegios/${reg.id || reg.Id}/estadisticas`)}
                    type="button"
                    disabled={!reg.id && !reg.Id}
                    title="Estadísticas"
                  >
                    <i className="fas fa-chart-line" />
                  </button>
                </td>
              )}
              {isGeneralAdmin && (
                <td className="actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => openConsumos(reg)}
                    type="button"
                    disabled={!reg.id && !reg.Id}
                    title="Consumos"
                  >
                    <i className="fas fa-chart-bar" />
                  </button>
                </td>
              )}
              {isGeneralAdmin && (
                <td className="actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => (reg.id || reg.Id) && navigate(`/admin/colegios/${reg.id || reg.Id}/openai-assistants-config`)}
                    type="button"
                    disabled={!reg.id && !reg.Id}
                    title="Asistentes OpenAI fijos"
                  >
                    <i className="fas fa-robot" />
                  </button>
                </td>
              )}
              {isGeneralAdmin && (
                <td className="actions">
                  <button
                    className="btn btn-danger"
                    onClick={() => openResetSchemaModal(reg)}
                    type="button"
                    disabled={!reg.id && !reg.Id}
                    title="Resetear esquema"
                  >
                    <i className="fas fa-rotate-left" />
                  </button>
                </td>
              )}
              {isGeneralAdmin && (
                <td className="actions">
                  <button
                    className={`btn ${activo ? 'btn-danger' : 'btn-success'}`}
                    onClick={() => handleToggleColegioActivo(reg)}
                    type="button"
                    disabled={!reg.id && !reg.Id}
                    title={activo ? 'Desactivar colegio' : 'Activar colegio'}
                  >
                    <i className={`fas ${activo ? 'fa-ban' : 'fa-check'}`} />
                  </button>
                </td>
              )}
              <td className="actions">
                <button className="btn btn-primary" onClick={() => handleSelect(reg)} type="button" title="Administrar">
                  Administrar
                  <i className="fas fa-arrow-right" />
                </button>
              </td>
            </tr>
          );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderParticularesTable = () => (
    <div className="admin-table-wrapper">
      <table id="admin-select-particulares-table" className="admin-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Administrador</th>
            <th>Email</th>
            <th>Administradores</th>
            <th>Profesores</th>
            <th>Configuración</th>
            <th>Editar</th>
            <th>Estado</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filteredParticulares.map((reg) => {
            const activo = Boolean(reg?.activo ?? reg?.Activo ?? true);
            const particularId = reg.id || reg.Id;
            return (
              <tr key={particularId || reg.email || reg.Email || reg.nombre || reg.Nombre} className={activo ? '' : 'row-inactive'}>
                <td>{getParticularNombreVisible(reg)}</td>
                <td>{getParticularAdministrador(reg) || '-'}</td>
                <td>{reg.email || reg.Email || '-'}</td>
                <td className="actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => particularId && navigate(`/admin/particulares/${particularId}/administradores`)}
                    type="button"
                    disabled={!particularId}
                    title="Administradores"
                  >
                    <i className="fas fa-user-shield" />
                  </button>
                </td>
                <td className="actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => particularId && navigate(`/admin/particulares/${particularId}/profesores`)}
                    type="button"
                    disabled={!particularId}
                    title="Profesores"
                  >
                    <i className="fas fa-user-plus" />
                  </button>
                </td>
                <td className="actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => openParticularConfig(reg)}
                    type="button"
                    disabled={!particularId}
                    title="Configuración"
                  >
                    <i className="fas fa-cog" />
                  </button>
                </td>
                <td className="actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => particularId && navigate(`/admin/particulares/${particularId}/editar`)}
                    type="button"
                    disabled={!particularId}
                    title="Editar"
                  >
                    <i className="fas fa-pen" />
                  </button>
                </td>
                <td className="actions">
                  <button
                    className={`btn ${activo ? 'btn-danger' : 'btn-success'}`}
                    onClick={() => handleToggleParticularActivo(reg)}
                    type="button"
                    disabled={!particularId}
                    title={activo ? 'Desactivar particular' : 'Activar particular'}
                  >
                    <i className={`fas ${activo ? 'fa-ban' : 'fa-check'}`} />
                  </button>
                </td>
                <td className="actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => handleAdministrarParticular(reg)}
                    type="button"
                    disabled={!particularId}
                    title="Administrar"
                  >
                    Administrar
                    <i className="fas fa-arrow-right" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const currentLoading = isParticularesScope ? particularesLoading : isLoading;
  const currentError = isParticularesScope ? particularesError : error;
  const currentItems = isParticularesScope ? particulares : colegios;
  const currentFilteredItems = isParticularesScope ? filteredParticulares : filteredColegios;
  const currentSearchPlaceholder = isParticularesScope ? 'Buscar particular...' : 'Buscar colegio...';
  const currentExportSelector = isParticularesScope ? '#admin-select-particulares-table' : '#admin-select-colegio-table';
  const currentExportFileName = isParticularesScope ? 'profesores-particulares' : 'colegios';
  const currentExportTitle = isParticularesScope ? 'Listado de particulares' : 'Listado de colegios';
  const currentEmptyText = isParticularesScope ? 'No hay particulares disponibles.' : 'No hay colegios disponibles.';
  const currentLoadingText = isParticularesScope ? 'Cargando particulares...' : 'Cargando colegios...';

  const pwaUpdateAppOption = getPwaAppOption(pwaUpdateModal.appKey);

  return (
    <>
      {accessingColegio.loading && (
        <LoadingSpinner
          message={`Accediendo ${isParticularesScope ? 'al particular' : 'al colegio'} ${accessingColegio.nombre}...`}
          size="medium"
          color="primary"
        />
      )}
      <div className="admin-select-colegio">
      <header className="page-header">
        <div>
          <p className="eyebrow">Modo administrador</p>
          <h1>{isParticularesScope ? 'Profesores particulares' : 'Seleccioná el colegio para trabajar'}</h1>
          <p className="subtitle">
            {isParticularesScope
              ? 'Alta y seguimiento de profesores particulares disponibles en el sistema.'
              : 'Debes elegir un colegio antes de acceder al dashboard de administración.'}
          </p>
        </div>
        <div className="header-actions">
          {isGeneralAdmin && (
            <button
              type="button"
              className="metrics-btn-header"
              onClick={openPwaUpdateModal}
              title="Forzar actualización de PWA"
            >
              <i className="fas fa-mobile-alt" />
              <span>Actualizar PWA</span>
            </button>
          )}
          {isGeneralAdmin && (
            <button
              type="button"
              className="metrics-btn-header"
              onClick={() => setShowComunicacionesModal(true)}
              title="Comunicaciones"
            >
              <i className="fas fa-paper-plane" />
              <span>Comunicaciones</span>
            </button>
          )}
          {isGeneralAdmin && (
            <button
              type="button"
              className="metrics-btn-header"
              onClick={() => navigate('/admin/metricas-generales')}
              title="Métricas generales"
            >
              <i className="fas fa-chart-pie" />
              <span>Métricas generales</span>
            </button>
          )}
          {isGeneralAdmin && (
            <button
              type="button"
              className="metrics-btn-header"
              onClick={() => navigate('/admin/errores-generales')}
              title="Errores generales"
            >
              <i className="fas fa-bug" />
              <span>Errores generales</span>
            </button>
          )}
          {isGeneralAdmin && (
            <button
              type="button"
              className="metrics-btn-header"
              onClick={() => navigate('/admin/usuarios-en-linea')}
              title="Usuarios en línea"
            >
              <i className="fas fa-signal" />
              <span>Usuarios en línea</span>
            </button>
          )}
          {isGeneralAdmin && (
            <button
              type="button"
              className="inbox-btn-header"
              onClick={handleOpenAccessInbox}
              title="Mensajes de solicitud"
            >
              <i className="fas fa-inbox" />
              <span>Mensajes de solicitud</span>
              {accessUnreadCount > 0 && (
                <span className="inbox-badge">{accessUnreadCount}</span>
              )}
            </button>
          )}
          <ActiveColegioBadge />
          <button
            type="button"
            className="logout-btn-header"
            onClick={logout}
            title="Cerrar sesión"
          >
            <i className="fas fa-sign-out-alt" />
            <span>Salir</span>
          </button>
        </div>
      </header>

      <div className="toolbar">
        {isGeneralAdmin && (
          <div className="view-toggle entity-scope-toggle" aria-label="Tipo de administración">
            <button
              className={!isParticularesScope ? 'active' : ''}
              onClick={() => handleEntityScopeChange('colegios')}
              type="button"
            >
              <i className="fas fa-school" /> Colegios
            </button>
            <button
              className={isParticularesScope ? 'active' : ''}
              onClick={() => handleEntityScopeChange('particulares')}
              type="button"
            >
              <i className="fas fa-user-tie" /> Particulares
            </button>
          </div>
        )}
        <div className="search-box">
          <i className="fas fa-search" />
          <input
            type="text"
            placeholder={currentSearchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="toolbar-actions">
          {viewMode === 'table' && (
            <TableExportActions
              tableSelector={currentExportSelector}
              fileName={currentExportFileName}
              title={currentExportTitle}
              compact
              disabled={currentFilteredItems.length === 0}
            />
          )}
          {isGeneralAdmin && (
            <button
              className="new-colegio-btn"
              onClick={() => navigate(isParticularesScope ? '/admin/particulares/nuevo' : '/admin/colegios/nuevo')}
              type="button"
            >
              <i className="fas fa-plus" /> {isParticularesScope ? 'Alta particular' : 'Alta de colegio'}
            </button>
          )}
          <div className="view-toggle">
            <button
              className={viewMode === 'cards' ? 'active' : ''}
              onClick={() => setViewMode('cards')}
              type="button"
            >
              <i className="fas fa-th-large" /> Tarjetas
            </button>
            <button
              className={viewMode === 'table' ? 'active' : ''}
              onClick={() => setViewMode('table')}
              type="button"
            >
              <i className="fas fa-table" /> Tabla
            </button>
          </div>
        </div>
      </div>

      {currentLoading && <div className="state">{currentLoadingText}</div>}
      {currentError && <div className="state error">{currentError}</div>}
      {actionMessage.text && (
        <div className={`state ${actionMessage.type}`}>{actionMessage.text}</div>
      )}

      {!currentLoading && !currentError && currentItems.length === 0 && (
        <div className="state">{currentEmptyText}</div>
      )}

      {!currentLoading && !currentError && currentItems.length > 0 && (
        isParticularesScope
          ? (viewMode === 'table' ? renderParticularesTable() : renderParticularesCards())
          : (viewMode === 'table' ? renderTable() : renderCards())
      )}

      {!currentLoading && !currentError && !isParticularesScope && (
        <section className="select-colegio-footer-panel">
          <div className="select-colegio-footer-copy">
            <p className="eyebrow">Seguimiento operativo</p>
            <h3>Procesos asíncronos</h3>
            <p>
              Revisa desde aquí el estado de importaciones, comunicaciones por email y cualquier otro proceso lanzado por tu usuario, sin entrar primero a un colegio.
            </p>
          </div>
          <button
            type="button"
            className="select-colegio-footer-btn"
            onClick={() => {
              setFocusProcesoId(null);
              setShowProcesosModal(true);
            }}
          >
            <i className="fas fa-tasks" />
            <span>Ver procesos asíncronos</span>
          </button>
        </section>
      )}

      {ffModal.open && (
        <div className="ff-modal-overlay" onClick={closeFeatureFlags}>
          <div className="ff-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ff-modal-header">
              <div>
                <p className="ff-modal-eyebrow">Feature flags</p>
                <h3>{ffModal.colegio?.nombre || ffModal.colegio?.Nombre || 'Colegio'}</h3>
              </div>
              <button className="ff-modal-close" onClick={closeFeatureFlags} type="button" title="Cerrar">
                <i className="fas fa-times" />
              </button>
            </div>

            {ffModal.loading && (
              <div className="ff-modal-state">
                <i className="fas fa-spinner fa-spin" />
                <span>Cargando feature flags...</span>
              </div>
            )}

            {!ffModal.loading && ffModal.error && (
              <div className="ff-modal-error">{ffModal.error}</div>
            )}

            {!ffModal.loading && !ffModal.error && (
              <div className="ff-modal-list">
                {ffModal.items.length === 0 && (
                  <div className="ff-empty">No hay feature flags cargadas.</div>
                )}
                {ffModal.items.map((flag) => {
                  const nombre = flag?.nombre || flag?.Nombre || '';
                  const descripcion = flag?.descripcion || flag?.Descripcion || '';
                  const activo = Boolean(flag?.activo ?? flag?.Activo);
                  return (
                    <div key={nombre} className="ff-item">
                      <div className="ff-info">
                        <h4>{nombre}</h4>
                        <p>{descripcion || 'Sin descripción'}</p>
                      </div>
                      <label className="ff-toggle">
                        <input
                          type="checkbox"
                          checked={activo}
                          onChange={() => handleToggleFeatureFlag(flag)}
                        />
                        <span className="ff-slider" />
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {consumosModal.open && (
        <div className="ff-modal-overlay" onClick={closeConsumos}>
          <div className="ff-modal consumos-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ff-modal-header">
              <div>
                <p className="ff-modal-eyebrow">Consumos</p>
                <h3>{consumosModal.colegio?.nombre || consumosModal.colegio?.Nombre || 'Colegio'}</h3>
              </div>
              <button className="ff-modal-close" onClick={closeConsumos} type="button" title="Cerrar">
                <i className="fas fa-times" />
              </button>
            </div>

            <form className="consumos-filter-bar" onSubmit={handleConsumosFilterSubmit}>
              <label>
                <span>Desde</span>
                <input
                  type="date"
                  name="from"
                  value={consumosModal.filters?.from || ''}
                  onChange={handleConsumosFilterChange}
                  disabled={consumosModal.loading}
                />
              </label>
              <label>
                <span>Hasta</span>
                <input
                  type="date"
                  name="to"
                  value={consumosModal.filters?.to || ''}
                  onChange={handleConsumosFilterChange}
                  disabled={consumosModal.loading}
                />
              </label>
              <button className="btn btn-secondary" type="submit" disabled={consumosModal.loading}>
                <i className={`fas ${consumosModal.loading ? 'fa-spinner fa-spin' : 'fa-search'}`} />
                Consultar
              </button>
            </form>

            {consumosModal.loading && (
              <div className="ff-modal-state">
                <i className="fas fa-spinner fa-spin" />
                <span>Cargando consumos...</span>
              </div>
            )}

            {!consumosModal.loading && consumosModal.error && (
              <div className="ff-modal-error">{consumosModal.error}</div>
            )}

            {!consumosModal.loading && !consumosModal.error && (() => {
              const data = consumosModal.data || {};
              const get = (keys, fallback = 0) => readFirstValue(data, keys) ?? fallback;
              const filters = consumosModal.filters || getDefaultConsumosRange();
              const year = get(['year', 'Year'], new Date().getFullYear());
              const assistantRows = get([
                'consumosOpenAIPorAsistenteRango',
                'ConsumosOpenAIPorAsistenteRango',
                'consumosOpenAIPorAsistenteMesActual',
                'ConsumosOpenAIPorAsistenteMesActual'
              ], []);
              const legacyTokens = Number(get(['tokensLegacyRango', 'TokensLegacyRango', 'tokensLegacyMesActual', 'TokensLegacyMesActual'], 0));

              return (
                <div className="consumos-content">
                  <p className="consumos-text">
                    Detalle del rango:{' '}
                    <strong>{formatDateInputLabel(filters.from)} a {formatDateInputLabel(filters.to)}</strong>
                  </p>

                  <div className="consumos-kpi-grid">
                    <div className="consumos-kpi-card">
                      <span>Tokens enviados</span>
                      <strong>{formatNumber(get(['enviadosRango', 'EnviadosRango', 'enviadosMesActual', 'EnviadosMesActual', 'inputTokensRango', 'InputTokensRango', 'inputTokensMesActual', 'InputTokensMesActual']))}</strong>
                    </div>
                    <div className="consumos-kpi-card">
                      <span>Tokens recibidos</span>
                      <strong>{formatNumber(get(['recibidosRango', 'RecibidosRango', 'recibidosMesActual', 'RecibidosMesActual', 'outputTokensRango', 'OutputTokensRango', 'outputTokensMesActual', 'OutputTokensMesActual']))}</strong>
                    </div>
                    <div className="consumos-kpi-card">
                      <span>Total tokens</span>
                      <strong>{formatNumber(get(['totalRango', 'TotalRango', 'totalMesActual', 'TotalMesActual']))}</strong>
                    </div>
                    <div className="consumos-kpi-card cost">
                      <span>Costo aprox.</span>
                      <strong>{formatUsd(get(['costoUsdRango', 'CostoUsdRango', 'costoUsdMesActual', 'CostoUsdMesActual']))}</strong>
                    </div>
                  </div>

                  <div className="consumos-summary">
                    <span>Consultas OpenAI: {formatNumber(get(['requestsOpenAIRango', 'RequestsOpenAIRango', 'requestsOpenAIMesActual', 'RequestsOpenAIMesActual']))}</span>
                    <span>Cache entrada: {formatNumber(get(['cachedInputTokensRango', 'CachedInputTokensRango', 'cachedInputTokensMesActual', 'CachedInputTokensMesActual']))}</span>
                    {legacyTokens > 0 && <span>Tokens históricos sin costo: {formatNumber(legacyTokens)}</span>}
                  </div>

                  <div className="consumos-section-title">Detalle por asistente OpenAI</div>
                  {Array.isArray(assistantRows) && assistantRows.length > 0 ? (
                    <div className="consumos-table-wrap">
                      <table className="consumos-table">
                        <thead>
                          <tr>
                            <th>Asistente</th>
                            <th>Modelo</th>
                            <th>Consultas</th>
                            <th>Enviados</th>
                            <th>Recibidos</th>
                            <th>Total</th>
                            <th>Costo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assistantRows.map((row) => {
                            const rowGet = (keys, fallback = '') => readFirstValue(row, keys) ?? fallback;
                            const id = rowGet(['idAssistantConfig', 'IdAssistantConfig'], rowGet(['assistantKey', 'AssistantKey']));
                            return (
                              <tr key={id}>
                                <td>
                                  <strong>{rowGet(['assistantNombre', 'AssistantNombre'], rowGet(['assistantKey', 'AssistantKey'], 'Asistente'))}</strong>
                                  <small>{rowGet(['assistantKey', 'AssistantKey'], '')}</small>
                                </td>
                                <td>{rowGet(['modeloOpenAI', 'ModeloOpenAI'], '—')}</td>
                                <td>{formatNumber(rowGet(['requestsCount', 'RequestsCount'], 0))}</td>
                                <td>{formatNumber(rowGet(['inputTokens', 'InputTokens'], 0))}</td>
                                <td>{formatNumber(rowGet(['outputTokens', 'OutputTokens'], 0))}</td>
                                <td>{formatNumber(rowGet(['totalTokens', 'TotalTokens'], 0))}</td>
                                <td>{formatUsd(rowGet(['totalCostUsd', 'TotalCostUsd'], 0))}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="consumos-empty">No hay consumos OpenAI registrados para el rango seleccionado.</div>
                  )}

                  <div className="consumos-section-title">Evolución anual de tokens ({year})</div>
                  <div className="consumos-chart">
                    {(() => {
                      const consumos = data.consumosMensuales || data.ConsumosMensuales || [];
                      const normalized = monthLabels.map((label, index) => {
                        const mes = index + 1;
                        const item = consumos.find((entry) => entry.mes === mes || entry.Mes === mes) || {};
                        const enviados = item.enviados ?? item.Enviados ?? 0;
                        const recibidos = item.recibidos ?? item.Recibidos ?? 0;
                        const total = item.total ?? item.Total ?? (enviados + recibidos);
                        return { label, enviados, recibidos, total };
                      });
                      const maxTotal = Math.max(1, ...normalized.map((item) => item.total));
                      return normalized.map((item, index) => {
                        const heightPct = (item.total / maxTotal) * 100;
                        const enviadosPct = item.total ? (item.enviados / item.total) * 100 : 0;
                        const recibidosPct = item.total ? (item.recibidos / item.total) * 100 : 0;
                        return (
                          <div key={index} className="consumos-bar">
                            <div className="consumos-bar-track">
                              <div className="consumos-bar-fill" style={{ height: `${heightPct}%` }}>
                                <span className="consumos-segment recibidos" style={{ height: `${recibidosPct}%` }} />
                                <span className="consumos-segment enviados" style={{ height: `${enviadosPct}%` }} />
                              </div>
                            </div>
                            <span className="consumos-bar-label">{item.label}</span>
                            <span className="consumos-bar-value">{formatNumber(item.total)}</span>
                          </div>
                        );
                      });
                    })()}
                  </div>
                  <div className="consumos-legend">
                    <span><i className="legend-dot enviados" /> Enviados</span>
                    <span><i className="legend-dot recibidos" /> Recibidos</span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {particularConfigModal.open && (
        <div className="ff-modal-overlay" onClick={closeParticularConfig}>
          <div className="ff-modal particular-config-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ff-modal-header">
              <div>
                <p className="ff-modal-eyebrow">Configuración</p>
                <h3>{getParticularNombreVisible(particularConfigModal.particular)}</h3>
              </div>
              <button
                className="ff-modal-close"
                onClick={closeParticularConfig}
                type="button"
                title="Cerrar"
                disabled={particularConfigModal.saving}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <form className="particular-config-form" onSubmit={saveParticularConfig}>
              <div className="particular-config-grid">
                {PARTICULAR_LIMIT_FIELDS.map((field) => (
                  <label key={field.key} className="particular-config-field">
                    <span>
                      <i className={field.icon} />
                      {field.label}
                    </span>
                    <input
                      type="number"
                      name={field.key}
                      min="0"
                      step={field.step}
                      value={particularConfigModal.form[field.key] ?? ''}
                      onChange={handleParticularConfigChange}
                      placeholder="Sin límite"
                      disabled={particularConfigModal.saving}
                    />
                  </label>
                ))}
              </div>

              <label className="particular-config-toggle">
                <span>
                  <i className="fas fa-robot" />
                  <strong>Permite asistentes IA</strong>
                  <small>Habilita Asistentes IA y Reportes para esta cuenta particular.</small>
                </span>
                <input
                  type="checkbox"
                  name="permiteChatGPT"
                  checked={Boolean(particularConfigModal.form.permiteChatGPT)}
                  onChange={handleParticularConfigChange}
                  disabled={particularConfigModal.saving}
                />
                <em aria-hidden="true" />
              </label>

              <label className="particular-config-toggle">
                <span>
                  <i className="fas fa-calendar-alt" />
                  <strong>Calendario</strong>
                  <small>Habilita disponibilidad, clases y reservas para esta cuenta particular.</small>
                </span>
                <input
                  type="checkbox"
                  name="calendario"
                  checked={Boolean(particularConfigModal.form.calendario)}
                  onChange={handleParticularConfigChange}
                  disabled={particularConfigModal.saving}
                />
                <em aria-hidden="true" />
              </label>

              <label className="particular-config-toggle">
                <span>
                  <i className="fas fa-school" />
                  <strong>Modo colegio</strong>
                  <small>Organiza los cursos en años y divisiones y asigna los preceptores a cada división.</small>
                </span>
                <input
                  type="checkbox"
                  name="modoColegio"
                  checked={Boolean(particularConfigModal.form.modoColegio)}
                  onChange={handleParticularConfigChange}
                  disabled={particularConfigModal.saving}
                />
                <em aria-hidden="true" />
              </label>

              <label className="particular-config-toggle">
                <span>
                  <i className="fas fa-credit-card" />
                  <strong>Cobros Mercado Pago</strong>
                  <small>Habilita la configuración de cobros con Mercado Pago para esta cuenta particular.</small>
                </span>
                <input
                  type="checkbox"
                  name="cobrosMercadoPago"
                  checked={Boolean(particularConfigModal.form.cobrosMercadoPago)}
                  onChange={handleParticularConfigChange}
                  disabled={particularConfigModal.saving}
                />
                <em aria-hidden="true" />
              </label>

              <div className="particular-config-note">
                <i className="fas fa-info-circle" />
                <span>Los campos vacíos quedan sin límite. Si el uso actual supera un límite nuevo, no se borran datos existentes; se bloquean nuevas altas.</span>
              </div>

              {particularConfigModal.error && (
                <div className="ff-modal-error">{particularConfigModal.error}</div>
              )}

              <div className="confirm-modal-actions">
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={closeParticularConfig}
                  disabled={particularConfigModal.saving}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  type="submit"
                  disabled={particularConfigModal.saving}
                >
                  <i className={`fas ${particularConfigModal.saving ? 'fa-spinner fa-spin' : 'fa-save'}`} />
                  {particularConfigModal.saving ? 'Guardando...' : 'Guardar configuración'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmModal.open && (
        <div className="confirm-modal-overlay" onClick={closeConfirmModal}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <div>
                <p className="confirm-modal-eyebrow">Colegios</p>
                <h3>
                  {confirmModal.nextActivo ? 'Activar colegio' : 'Desactivar colegio'}
                </h3>
              </div>
              <button
                className="confirm-modal-close"
                onClick={closeConfirmModal}
                type="button"
                title="Cerrar"
                disabled={confirmModal.loading}
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <p className="confirm-modal-text">
              {confirmModal.nextActivo
                ? `Vas a activar el colegio "${confirmModal.colegio?.nombre || confirmModal.colegio?.name || 'sin nombre'}".`
                : `Vas a desactivar el colegio "${confirmModal.colegio?.nombre || confirmModal.colegio?.name || 'sin nombre'}".`}
            </p>
            <div className="confirm-modal-actions">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={closeConfirmModal}
                disabled={confirmModal.loading}
              >
                Cancelar
              </button>
              <button
                className={`btn ${confirmModal.nextActivo ? 'btn-success' : 'btn-danger'}`}
                type="button"
                onClick={confirmToggleColegio}
                disabled={confirmModal.loading}
              >
                {confirmModal.loading ? 'Procesando...' : confirmModal.nextActivo ? 'Activar' : 'Desactivar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetSchemaModal.open && (
        <div className="schema-reset-modal-overlay" onClick={closeResetSchemaModal}>
          <div className="schema-reset-modal" onClick={(e) => e.stopPropagation()}>
            <div className="schema-reset-modal-header">
              <div>
                <p className="schema-reset-modal-eyebrow">Acción crítica</p>
                <h3>Resetear esquema del colegio</h3>
              </div>
              <button
                className="schema-reset-modal-close"
                onClick={closeResetSchemaModal}
                type="button"
                title="Cerrar"
                disabled={resetSchemaModal.loading}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="schema-reset-modal-alert">
              <i className="fas fa-triangle-exclamation" />
              <div>
                <strong>Esta acción elimina el esquema actual del colegio y lo vuelve a crear desde cero.</strong>
                <p>
                  Se conserva el registro público del colegio y su información básica actual, pero se borran los datos internos del schema.
                </p>
              </div>
            </div>

            <div className="schema-reset-modal-grid">
              <section className="schema-reset-modal-panel">
                <h4>Qué se pierde</h4>
                <ul>
                  <li>Áreas, divisiones, materias, cursos, aulas y asistentes del colegio.</li>
                  <li>Relaciones académicas, configuraciones internas y procesos del schema.</li>
                  <li>Cualquier dato cargado dentro del esquema actual que no esté en las tablas públicas.</li>
                </ul>
              </section>

              <section className="schema-reset-modal-panel">
                <h4>Qué puede pasar después</h4>
                <ul>
                  <li>La acción no se puede deshacer.</li>
                  <li>Si hay procesos en curso o usuarios trabajando, pueden fallar después del reset.</li>
                  <li>Puede dejar archivos o recursos externos previamente creados sin vínculo con la nueva base.</li>
                </ul>
              </section>
            </div>

            <label className="schema-reset-modal-checkbox">
              <input
                type="checkbox"
                checked={resetSchemaModal.understood}
                onChange={(e) => setResetSchemaModal((prev) => ({ ...prev, understood: e.target.checked }))}
                disabled={resetSchemaModal.loading}
              />
              <span>Entiendo que esta acción es destructiva, irreversible y puede afectar al colegio completo.</span>
            </label>

            <div className="schema-reset-modal-confirmation">
              <label htmlFor="schema-reset-confirm-input">
                Para habilitar el reset, escribí exactamente el nombre actual del colegio:
                {' '}
                <strong>{getResetSchemaColegioNombre() || 'Sin nombre'}</strong>
              </label>
              <input
                id="schema-reset-confirm-input"
                type="text"
                value={resetSchemaModal.confirmText}
                onChange={(e) => setResetSchemaModal((prev) => ({ ...prev, confirmText: e.target.value }))}
                disabled={resetSchemaModal.loading}
                placeholder="Escribí el nombre exacto del colegio"
                autoComplete="off"
              />
              {resetSchemaModal.confirmText.trim() && (
                <p className={`schema-reset-modal-hint ${isResetSchemaNameMatch ? 'ok' : 'error'}`}>
                  {isResetSchemaNameMatch
                    ? 'Confirmación válida. Ya puedes ejecutar el reset.'
                    : 'La confirmación no coincide con el nombre actual del colegio.'}
                </p>
              )}
            </div>

            <div className="confirm-modal-actions">
              <button
                className="btn btn-secondary"
                type="button"
                onClick={closeResetSchemaModal}
                disabled={resetSchemaModal.loading}
              >
                Cancelar
              </button>
              <button
                className="btn btn-danger"
                type="button"
                onClick={confirmResetSchema}
                disabled={!canConfirmResetSchema || resetSchemaModal.loading}
              >
                {resetSchemaModal.loading ? 'Reseteando...' : 'Sí, resetear esquema'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pwaUpdateModal.open && (
        <div className="pwa-update-modal-overlay" onClick={closePwaUpdateModal}>
          <div className="pwa-update-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pwa-update-modal-header">
              <div>
                <p className="pwa-update-modal-eyebrow">PWA móvil</p>
                <h3>Actualizar {pwaUpdateAppOption.title}</h3>
              </div>
              <button
                className="pwa-update-modal-close"
                onClick={closePwaUpdateModal}
                type="button"
                title="Cerrar"
                disabled={pwaUpdateModal.saving}
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="pwa-update-modal-alert">
              <i className="fas fa-rotate" />
              <div>
                <strong>Esto no borra la sesión del usuario.</strong>
                <p>
                  Se enviará una notificación especial a la PWA elegida. En esa app se abrirá una modal para aceptar y recargar.
                </p>
              </div>
            </div>

            <section className="pwa-update-app-section">
              <span>Elegí la PWA a actualizar</span>
              <div className="pwa-update-app-options">
                {PWA_APP_OPTIONS.map((option) => {
                  const selected = option.key === pwaUpdateModal.appKey;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      className={`pwa-update-app-option ${selected ? 'active' : ''}`}
                      onClick={() => handlePwaUpdateAppChange(option.key)}
                      disabled={pwaUpdateModal.saving || pwaUpdateModal.loading}
                      aria-pressed={selected}
                    >
                      <i className={`fas ${option.key === 'familia-pwa' ? 'fa-users' : 'fa-comments'}`} />
                      <div>
                        <strong>{option.label}</strong>
                        <small>{option.title}</small>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {pwaUpdateModal.loading ? (
              <div className="ff-modal-state">
                <i className="fas fa-spinner fa-spin" />
                <span>Cargando configuración actual...</span>
              </div>
            ) : (
              <>
                {pwaUpdateModal.error && (
                  <div className="ff-modal-error">{pwaUpdateModal.error}</div>
                )}

                <label className="pwa-update-field">
                  <span>Mensaje para mostrar en {pwaUpdateAppOption.label}</span>
                  <textarea
                    rows={3}
                    value={pwaUpdateModal.mensaje}
                    onChange={(e) => setPwaUpdateModal((prev) => ({ ...prev, mensaje: e.target.value }))}
                    disabled={pwaUpdateModal.saving}
                  />
                </label>

                <div className="confirm-modal-actions">
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={closePwaUpdateModal}
                    disabled={pwaUpdateModal.saving}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={savePwaUpdateConfig}
                    disabled={pwaUpdateModal.saving}
                  >
                    {pwaUpdateModal.saving
                      ? 'Guardando...'
                      : 'Forzar actualización ahora'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {pwaUpdateResultModal.open && (
        <div className="pwa-update-modal-overlay" onClick={closePwaUpdateResultModal}>
          <div className="pwa-update-modal pwa-update-result-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pwa-update-modal-header">
              <div>
                <p className="pwa-update-modal-eyebrow">PWA móvil</p>
                <h3>{pwaUpdateResultModal.title}</h3>
              </div>
              <button
                className="pwa-update-modal-close"
                onClick={closePwaUpdateResultModal}
                type="button"
                title="Cerrar"
              >
                <i className="fas fa-times" />
              </button>
            </div>

            <div className={`pwa-update-result-status ${pwaUpdateResultModal.type}`}>
              <i
                className={`fas ${
                  pwaUpdateResultModal.type === 'error'
                    ? 'fa-circle-exclamation'
                    : pwaUpdateResultModal.type === 'warning'
                      ? 'fa-triangle-exclamation'
                      : 'fa-circle-check'
                }`}
              />
              <div>
                {pwaUpdateResultModal.lines.map((line, index) => (
                  <p key={`${line}-${index}`}>{line}</p>
                ))}
              </div>
            </div>

            <div className="confirm-modal-actions">
              <button
                className="btn btn-primary"
                type="button"
                onClick={closePwaUpdateResultModal}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {showAccessInbox && (
        <div className="access-inbox-overlay" onClick={handleCloseAccessInbox}>
          <div className="access-inbox-card" onClick={(e) => e.stopPropagation()}>
            <div className="access-inbox-header">
              <div>
                <h3>Mensajes de solicitud</h3>
                <p>{accessTotal} mensajes • {accessUnreadCount} sin leer</p>
              </div>
              <button className="close-btn" onClick={handleCloseAccessInbox}>
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="access-inbox-toolbar">
              <div className="access-search">
                <i className="fas fa-search" />
                <input
                  type="text"
                  placeholder="Buscar por nombre, apellido, email o texto..."
                  value={accessSearch}
                  onChange={(e) => setAccessSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="access-inbox-body">
              <div className="access-inbox-list">
                {accessLoading && <div className="access-inbox-loading">Cargando...</div>}
                {accessError && <div className="access-inbox-error">{accessError}</div>}
                {!accessLoading && filteredAccessRequests.length === 0 && (
                  <div className="access-inbox-empty">No hay mensajes.</div>
                )}
                {filteredAccessRequests.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`access-inbox-item ${item.leido ? 'is-read' : 'is-unread'} ${accessSelected?.id === item.id ? 'is-active' : ''}`}
                    onClick={() => handleSelectAccessRequest(item)}
                  >
                    <div className="access-item-header">
                      <span className="access-item-name">{item.nombre} {item.apellido}</span>
                      <span className={`access-item-badge ${item.leido ? 'read' : 'unread'}`}>
                        {item.leido ? 'Leído' : 'No leído'}
                      </span>
                    </div>
                    <div className="access-item-meta">
                      <span>{item.email}</span>
                      <span>{formatDateTime(item.fechaRecibido)}</span>
                    </div>
                    <div className="access-item-preview">{item.consulta}</div>
                  </button>
                ))}

                {accessRequests.length < accessTotal && (
                  <button
                    type="button"
                    className="access-load-more"
                    onClick={() => loadAccessRequests(accessPage + 1, true)}
                    disabled={accessLoadingMore}
                  >
                    {accessLoadingMore ? 'Cargando...' : 'Cargar 50 más'}
                  </button>
                )}
              </div>

              <div className="access-inbox-detail">
                {accessDetailLoading && (
                  <div className="access-inbox-loading">
                    <span className="access-loading-spinner" />
                    <span>Obteniendo información del mensaje...</span>
                  </div>
                )}
                {!accessDetailLoading && !accessSelected && (
                  <div className="access-inbox-empty">Selecciona un mensaje para ver el detalle.</div>
                )}
                {!accessDetailLoading && accessSelected && (
                  <div className="access-detail-card">
                    <div className="access-detail-header">
                      <div>
                        <h4>{accessSelected.nombre} {accessSelected.apellido}</h4>
                        <p>Solicitud de acceso</p>
                      </div>
                      <span className={`access-detail-status ${accessSelected.leido ? 'read' : 'unread'}`}>
                        {accessSelected.leido ? 'Leído' : 'No leído'}
                      </span>
                    </div>
                    <div className="access-detail-meta">
                      <div>
                        <span className="access-detail-label">Email</span>
                        <span>{accessSelected.email}</span>
                      </div>
                      <div>
                        <span className="access-detail-label">Teléfono</span>
                        <span>{accessSelected.telefono || '—'}</span>
                      </div>
                      <div>
                        <span className="access-detail-label">Recibido</span>
                        <span>{formatDateTime(accessSelected.fechaRecibido)}</span>
                      </div>
                      {accessSelected.fechaLeido && (
                        <div>
                          <span className="access-detail-label">Leído el</span>
                          <span>{formatDateTime(accessSelected.fechaLeido)}</span>
                        </div>
                      )}
                      {accessSelected.nombreUsuarioLeido && (
                        <div>
                          <span className="access-detail-label">Leído por</span>
                          <span>{accessSelected.nombreUsuarioLeido} {accessSelected.apellidoUsuarioLeido || ''}</span>
                        </div>
                      )}
                    </div>
                    <div className="access-detail-content">
                      <span className="access-detail-label">Consulta</span>
                      <p>{accessSelected.consulta}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminComunicacionesModal
        open={showComunicacionesModal}
          onClose={() => setShowComunicacionesModal(false)}
          colegios={colegios}
          onSuccess={({ message, processId, total }) => {
            setFocusProcesoId(processId || null);
            setShowProcesosModal(true);
            setActionMessage({
              type: 'success',
              text: `${message}${processId ? ` Proceso: ${processId}.` : ''}${total ? ` Destinatarios: ${total}.` : ''}`
            });
          }}
      />

      <ProcesosAsincronicosModal
        open={showProcesosModal}
        onClose={() => setShowProcesosModal(false)}
        focusProcesoId={focusProcesoId}
        usePublicScope
      />
    </div>
    </>
  );
};

export default AdminSelectColegio;
